import React from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ContactList = ({
  contacts,
  selectedContact,
  onContactSelect,
  searchQuery,
  onSearchChange,
  isTrainer,
  messages,
  currentUserId
}) => {
  const getLastMessage = (contactId) => {
    const contactMessages = messages?.filter(
      m => m?.senderId === contactId || m?.recipientId === contactId
    );
    return contactMessages?.[contactMessages?.length - 1];
  };

  const getUnreadCount = (contactId) => {
    return messages?.filter(
      m => m?.senderId === contactId && m?.recipientId === currentUserId && m?.status !== 'read'
    )?.length || 0;
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  return (
    <div
      className="col-span-12 md:col-span-4 rounded-xl overflow-hidden flex flex-col"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
    >
      {/* Header */}
      <div className="p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <h2
          className="font-bold flex items-center gap-2 mb-3"
          style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
        >
          <Icon name="Users" size={20} style={{ color: '#00D4FF' }} />
          {isTrainer ? 'Clients' : 'Trainer'}
          {contacts?.length > 0 && (
            <span
              className="ml-auto text-xs px-2 py-1 rounded-full"
              style={{ color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.06)' }}
            >
              {contacts?.length}
            </span>
          )}
        </h2>

        {/* Search Input */}
        {contacts?.length > 3 && (
          <div className="relative">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2"
              style={{ color: 'rgba(255,255,255,0.3)' }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              placeholder="Search contacts..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
              }}
            />
          </div>
        )}
      </div>

      {/* Contacts List */}
      <div className="overflow-y-auto flex-1">
        {contacts?.length > 0 ? (
          contacts?.map((contact) => {
            const lastMessage = getLastMessage(contact?.id);
            const unreadCount = getUnreadCount(contact?.id);
            const isSelected = selectedContact?.id === contact?.id;

            return (
              <button
                key={contact?.id}
                onClick={() => onContactSelect(contact)}
                className="w-full p-4 flex items-start gap-3 transition-all border-l-4"
                style={{
                  background: isSelected ? 'rgba(0,212,255,0.06)' : 'transparent',
                  borderLeftColor: isSelected ? '#00D4FF' : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                {/* Avatar with Status */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
                    style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                  >
                    {contact?.fullName?.charAt(0)}
                  </div>
                  <div
                    className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2"
                    style={{ background: '#AAFF00', borderColor: '#0A0A0B', boxShadow: '0 0 6px #AAFF0099' }}
                  />
                </div>

                {/* Contact Info */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold truncate" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
                      {contact?.fullName}
                    </p>
                    {lastMessage && (
                      <span className="text-xs ml-2 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        {formatLastMessageTime(lastMessage?.createdAt)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-sm truncate" style={{ color: unreadCount > 0 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)', fontWeight: unreadCount > 0 ? 600 : 400 }}>
                      {lastMessage ? (
                        <>
                          {lastMessage?.senderId === currentUserId && (
                            <Icon name="Check" size={14} className="inline mr-1" style={{ color: 'rgba(255,255,255,0.3)' }} />
                          )}
                          {lastMessage?.content}
                        </>
                      ) : (
                        'No messages yet'
                      )}
                    </p>
                    {unreadCount > 0 && (
                      <span
                        className="ml-2 flex-shrink-0 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                        style={{ background: '#00D4FF', color: '#0A0A0B', boxShadow: '0 0 8px #00D4FF88', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div className="p-8 text-center">
            <Icon name="Users" size={48} className="mx-auto mb-4" style={{ color: 'rgba(255,255,255,0.15)' }} />
            <p className="font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>No contacts found</p>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
              {searchQuery ? 'Try a different search' : 'No contacts available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactList;